
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { ConnectionInfo } from './ConnectionCard/ConnectionInfo';
import { ConnectionActions } from './ConnectionCard/ConnectionActions';
import { FeedProfileThumbnail } from '@/components/feed/FeedProfileThumbnail';
import { debugLogger } from '@/utils/debugLogger';
import { getProfileBorderStyle } from '@/utils/profileBorderUtils';

export interface Profile {
  id: string | number;
  name: string;
  username?: string;
  age: number;
  location: string;
  photo: string;
  occupation?: string;
  interests?: string[];
  mutual?: boolean;
  connectionDate?: string;
  plan_type?: string;
  verification_level?: string;
  is_online?: boolean;
}

interface ConnectionCardProps {
  profile: Profile;
  isMatch?: boolean;
  isSentLike?: boolean;
  isReceived?: boolean;
  onAction: (id: string | number, actionType: 'like' | 'unlike' | 'message') => void;
}

const ConnectionCard: React.FC<ConnectionCardProps> = ({
  profile,
  isMatch = false,
  isSentLike = false,
  isReceived = false,
  onAction
}) => {
  const [isDeleting, setIsDeleting] = useState(false);
  const navigate = useNavigate();

  debugLogger.debug('ConnectionCard', 'Perfil recebido', profile);

  const handleAction = async (actionType: 'like' | 'unlike' | 'message') => {
    if (isDeleting) return;
    
    setIsDeleting(true);
    debugLogger.debug('ConnectionCard', 'Action triggered', { profileId: profile.id, actionType });
    
    try {
      await onAction(profile.id, actionType);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCardClick = (e: React.MouseEvent) => {
    // Prevent navigation if clicking on buttons
    if ((e.target as HTMLElement).closest('button')) {
      return;
    }
    
    // Navigate to profile page
    const username = profile.username || profile.id;
    navigate(`/main/user/${username}`);
  };

  const isPremium = profile.plan_type && !['free', 'sponsor_free', 'sponsored_free'].includes(profile.plan_type);
  const isVerified = profile.verification_level === 'full';
  const borderStyle = getProfileBorderStyle(isPremium, isVerified, profile.is_online || false);

  return (
    <Card 
      className={`bg-black/40 overflow-hidden transition-all group cursor-pointer ${borderStyle.containerClass} ${borderStyle.animation || ''} hover:border-white/20`}
      onClick={handleCardClick}
    >
      <div className="flex gap-3 p-3">
        {/* Profile Photo */}
        <div className="relative w-20 h-20 flex-shrink-0">
          <FeedProfileThumbnail
            src={profile.photo}
            alt={profile.name}
            username={profile.username}
            className="w-full h-full rounded-lg"
          />
        </div>
        
        {/* Profile Info - Constrained to photo height */}
        <div className="flex-1 min-w-0 h-20 flex flex-col justify-between">
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-medium text-white truncate leading-tight">
              {profile.name}{profile.age > 0 && `, ${profile.age}`}
            </h3>
            <p className="text-white/70 text-xs truncate">{profile.location}</p>
            {profile.occupation && (
              <p className="text-white/50 text-xs truncate">{profile.occupation}</p>
            )}
          </div>
          
          <div className="mt-1">
            <ConnectionActions
              isMatch={isMatch}
              isSentLike={isSentLike}
              isReceived={isReceived}
              isDeleting={isDeleting}
              onLike={() => handleAction('like')}
              onMessage={() => handleAction('message')}
              onUnlike={() => handleAction('unlike')}
              onViewProfile={() => navigate(`/main/user/${profile.username || profile.id}`)}
            />
          </div>
        </div>
      </div>
    </Card>
  );
};

export default ConnectionCard;
